/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */
import { Button, Icon, Tooltip } from '@equinor/eds-core-react';
import {
  close as CLOSE,
  copy as COPY,
  delete_to_trash as DELETE,
  edit as EDIT,
  file_description as FILEDESC,
  play as PLAY,
  save as SAVE,
} from '@equinor/eds-icons';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ComputeJobStatus,
  ListComputeCasesByAnalogueModelIdQueryResponse,
} from '../../../../../api/generated';
import { ConfirmDialog } from '../../../../../components/ConfirmDialog/ConfirmDialog';
import * as Styled from './CaseButtons.styled';
import { useIsOwnerOrAdmin } from '../../../../../hooks/useIsOwnerOrAdmin';
import { useFetchModel } from '../../../../../hooks/useFetchModel';
import { LogSideSheet } from '../../LogSideSheet/LogSideSheet';

export const CaseButtons = ({
  id,
  caseType,
  saved,
  caseStatus,
  hasUnsavedCase,
  logFileExists,
  isEditing,
  hasChanges,
  onEditToggle,
  saveCase,
  runCase,
  deleteCase,
  removeLocalCase,
  setAlertMessage,
  duplicateCase,
  isVariogramExhausted,
}: {
  id: string;
  caseType: string;
  saved: boolean;
  caseStatus: ComputeJobStatus;
  hasUnsavedCase: boolean;
  logFileExists: boolean;
  isEditing: boolean;
  hasChanges: boolean;
  onEditToggle: () => void;
  runCase?: () => void;
  saveCase: () => void;
  deleteCase: (
    computeCaseId: string,
  ) => Promise<ListComputeCasesByAnalogueModelIdQueryResponse | undefined>;
  removeLocalCase: (id: string) => void;
  setAlertMessage: (message: string) => void;
  duplicateCase: () => void;
  isVariogramExhausted: boolean;
}) => {
  const { data: analogueModel } = useFetchModel();
  const isOwnerOrAdmin = useIsOwnerOrAdmin();
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [saveConfirm, setSaveConfirm] = useState(false);
  const [toggle, setToggle] = useState(false);
  const navigate = useNavigate();
  const handleConfirmSave = () => {
    saveCase();
    setSaveConfirm(false);
  };

  const handleConfirmDelete = async () => {
    if (!saved) {
      removeLocalCase(id);
      setAlertMessage('Case deleted');
      setDeleteConfirm(false);
      return;
    }
    const res = await deleteCase(id);
    if (res?.success) {
      setAlertMessage('Case deleted');
    }
    setDeleteConfirm(false);
  };

  const deleteTooltip = () => {
    if (!isOwnerOrAdmin)
      return 'Can not delete because you are not owner or admin.';
    return 'Can not delete unsaved case.';
  };

  const duplicateTooltip = () => {
    if (!isOwnerOrAdmin)
      return 'Can not duplicate because you are not owner or admin.';
  };

  const isRunning =
    caseStatus === 'Created' ||
    caseStatus === 'Waiting' ||
    caseStatus === 'Running';

  return (
    <Styled.ButtonDiv>
      {!isOwnerOrAdmin ? (
        <Tooltip title={deleteTooltip()}>
          <Button disabled variant="ghost_icon" aria-label="remove">
            <Icon data={DELETE} size={24}></Icon>
          </Button>
        </Tooltip>
      ) : (
        <Tooltip
          title={
            isRunning
              ? 'Cancel the running case to perform this action'
              : 'Delete this case and its results'
          }
        >
          <Button
            variant="ghost_icon"
            disabled={isEditing || isRunning}
            onClick={() =>
              id.length < 3 ? handleConfirmDelete() : setDeleteConfirm(true)
            }
            aria-label="remove"
          >
            <Icon data={DELETE} size={24}></Icon>
          </Button>
        </Tooltip>
      )}

      {caseType === 'Variogram' && (
        <>
          {id.length < 3 || !isOwnerOrAdmin ? (
            <Tooltip title={duplicateTooltip()}>
              <Button disabled variant="ghost_icon" aria-label="duplicate">
                <Icon data={COPY} size={24}></Icon>
              </Button>
            </Tooltip>
          ) : (
            <>
              {hasUnsavedCase || isVariogramExhausted ? (
                <Tooltip
                  title={
                    isVariogramExhausted
                      ? 'All variogram combinations for this variogram type are already created'
                      : 'Only one unsaved case per method at the time.'
                  }
                >
                  <Button disabled variant="ghost_icon" aria-label="duplicate">
                    <Icon data={COPY} size={24}></Icon>
                  </Button>
                </Tooltip>
              ) : (
                <Tooltip title="Duplicate this case with the same settings">
                  <Button
                    disabled={isEditing}
                    variant="ghost_icon"
                    aria-label="duplicate"
                    onClick={() => duplicateCase()}
                  >
                    <Icon data={COPY} size={24}></Icon>
                  </Button>
                </Tooltip>
              )}
            </>
          )}
        </>
      )}

      {caseType === 'Variogram' && saved && (
        <Tooltip
          title={
            isRunning
              ? 'Cancel the running case to perform this action'
              : isEditing
              ? 'Cancel editing'
              : 'Edit case'
          }
        >
          <Button
            variant="ghost_icon"
            onClick={onEditToggle}
            aria-label={isEditing ? 'cancel edit' : 'edit'}
            disabled={!isOwnerOrAdmin || isRunning}
          >
            <Icon data={isEditing ? CLOSE : EDIT} size={24}></Icon>
          </Button>
        </Tooltip>
      )}

      {caseType === 'Object' ? (
        <>
          {caseStatus === 'Succeeded' ? (
            <Styled.Button
              variant="outlined"
              onClick={() => {
                navigate('../results/object');
              }}
            >
              View Results{' '}
            </Styled.Button>
          ) : saved ? (
            <Tooltip
              title={
                !analogueModel?.isProcessed
                  ? 'Model not finished processed.'
                  : caseStatus === 'Created' ||
                    caseStatus === 'Waiting' ||
                    caseStatus === 'Running'
                  ? 'Case is running.'
                  : caseStatus === 'Failed'
                  ? 'The area selected for the script does not include architectural element that was provided when computing a case.'
                  : ''
              }
            >
              <Button
                color={
                  caseStatus === 'Created' ||
                  caseStatus === 'Failed' ||
                  caseStatus === 'Waiting' ||
                  caseStatus === 'Running'
                    ? 'danger'
                    : undefined
                }
                variant="outlined"
                onClick={saved ? runCase : saveCase}
                disabled={
                  !analogueModel?.isProcessed ||
                  caseStatus === 'Created' ||
                  !isOwnerOrAdmin
                }
              >
                <Icon data={PLAY} size={18}></Icon>
                {caseStatus === 'Created' ||
                caseStatus === 'Waiting' ||
                caseStatus === 'Running'
                  ? 'Cancel'
                  : caseStatus === 'Failed'
                  ? 'Re-run'
                  : 'Run'}
              </Button>
            </Tooltip>
          ) : (
            <Button variant="outlined" onClick={saved ? runCase : saveCase}>
              <Icon data={SAVE} size={18}></Icon>
              Save
            </Button>
          )}
        </>
      ) : (
        <>
          <Tooltip
            title={
              !analogueModel?.isProcessed
                ? 'Model not finished processed.'
                : caseStatus === 'Created' ||
                  caseStatus === 'Waiting' ||
                  caseStatus === 'Running'
                ? 'Case are running.'
                : caseStatus === 'Failed'
                ? 'The area selected for the script does not include architectural element that was provided when computing a case.'
                : ''
            }
          >
            {caseStatus === 'Succeeded' ? (
              <Styled.Button
                variant="outlined"
                disabled={isEditing || isRunning}
                onClick={() => {
                  navigate('../results/variogram');
                }}
              >
                View Results{' '}
              </Styled.Button>
            ) : (
              <Styled.Button
                color={
                  caseStatus === 'Created' ||
                  caseStatus === 'Failed' ||
                  caseStatus === 'Waiting' ||
                  caseStatus === 'Running'
                    ? 'danger'
                    : undefined
                }
                variant="outlined"
                onClick={runCase}
                disabled={
                  (isEditing && !isRunning) ||
                  !analogueModel?.isProcessed ||
                  id.length < 3 ||
                  !isOwnerOrAdmin
                }
              >
                <Icon data={PLAY} size={18}></Icon>
                {caseStatus === 'Created' ||
                caseStatus === 'Waiting' ||
                caseStatus === 'Running'
                  ? 'Cancel'
                  : caseStatus === 'Failed'
                  ? 'Re-run'
                  : 'Run'}
              </Styled.Button>
            )}
          </Tooltip>
          {logFileExists &&
            (caseStatus === 'Succeeded' || caseStatus === 'Failed') && (
              <Tooltip
                title={
                  isRunning
                    ? 'Cancel the running case to perform this action'
                    : undefined
                }
              >
                <Styled.Button
                  variant="outlined"
                  onClick={() => setToggle(!toggle)}
                  disabled={isEditing || isRunning}
                >
                  <Icon data={FILEDESC} size={18}></Icon>
                  Log
                </Styled.Button>
              </Tooltip>
            )}
          <Tooltip
            title={
              isRunning
                ? 'Cancel the running case to perform this action'
                : undefined
            }
          >
            <Button
              disabled={!isOwnerOrAdmin || isRunning || (saved && !hasChanges)}
              variant="outlined"
              onClick={id.length > 3 ? () => setSaveConfirm(true) : saveCase}
            >
              <Icon data={SAVE} size={18}></Icon>
              Save
            </Button>
          </Tooltip>
        </>
      )}
      {deleteConfirm && (
        <ConfirmDialog
          isOpen={deleteConfirm}
          message="By pressing OK, the case and belonging results will be deleted."
          danger={true}
          confirmAction={handleConfirmDelete}
          setIsOpen={setDeleteConfirm}
        ></ConfirmDialog>
      )}
      {saveConfirm && (
        <ConfirmDialog
          isOpen={saveConfirm}
          message="By pressing OK, the current case will be overwritten, deleting old
      results."
          danger={false}
          confirmAction={handleConfirmSave}
          setIsOpen={setSaveConfirm}
        ></ConfirmDialog>
      )}
      {logFileExists && toggle && (
        <LogSideSheet
          toggle={toggle}
          setToggle={setToggle}
          computeCaseId={id}
        ></LogSideSheet>
      )}
    </Styled.ButtonDiv>
  );
};
